<template>
  <div class="flex">
    <div id="number-autocomplete" class="header-input">
      <el-autocomplete
        ref="autoComplete"
        v-model="number"
        value-key="nameNumber"
        :fetch-suggestions="suggestContacts"
        placeholder="Enter Name or Number"
        clearable
        :trigger-on-focus="false"
        @select="handleSelect"
        @clear="clearContact">
        <template slot-scope="scope">
          <contact
            v-if="!scope.item.hasOwnProperty('COMPANY') && !scope.item.hasOwnProperty('contactType')"
            :name="scope.item.name"
            :number="null"
            :email="scope.item.email"
            :status="scope.item.extStatus"
            :dnd="scope.item.dnd"
            :branch="scope.item.branch"
            :department="scope.item.department"
            :avatar="scope.item.avatarPath">
          </contact>
          <personal-contact
            v-if="scope.item.hasOwnProperty('COMPANY')"
            :firstName="scope.item.FIRSTNAME"
            :lastName="scope.item.LASTNAME"
            :number="null"
            :email="scope.item.EMAIL"
            :company="scope.item.COMPANY"
            :show-company="false">
          </personal-contact>
          <other-contact
            v-if="scope.item.hasOwnProperty('contactType')"
            class="contact-container"
            :type="scope.item.contactType"
            :name="scope.item.name"
            :number="scope.item.number"
          />
        </template>
      </el-autocomplete>
    </div>
    <div class="button-container">
      <div class="button-wrapper call-btn">
        <el-tooltip :content="!softphoneEnabled ? 'Call from Deskphone' : 'Call From Softphone'" placement="top">
          <el-button v-if="!softphoneEnabled" @click="() => {dialCall}" class="table-action-button icon-blue">
            <v-svg sprite="telephone"/>
          </el-button>
          <el-button v-else @click="() => {dialCall()}" class="table-action-button icon-blue">
            <v-svg sprite="headset"/>
          </el-button>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex';
  import feathersClient from "@/feathers/feathers-client";
  import Contact from "@/views/hudportal/contacts/components/contact";
  import PersonalContact from "@/views/hudportal/contacts/components/personal-contact";
  import OtherContact from "@/views/hudportal/softphone/components/other-contact";
  import {globalEventBus} from "@/globalEventBus";

  export default {
    name: 'Keypad',
    components: {Contact, PersonalContact, OtherContact},
    data() {
      return {
        number: '',
        selectedPerson: null,
        inputElement: null,
        currentSuggestions: []
      };
    },
    computed: {
      ...mapGetters('phonebook-entries', {getPhonebookEntries: 'find'}),
      user(){
        return this.$store.state.auth.user;
      },
      userHostServer(){
        return this.$store.state.userHostServer;
      },
      personalDirectory() {
        return this.getPhonebookEntries({
          query: {
            hasDetails: true,
            phonebookId: this.user.myExtension.omniaPhonebookId,
            $sort: {
              FIRSTNAME: 1
            }
          }
        }).data;
      },
      softphoneEnabled(){
        return this.$store.state.softphoneEnabled;
      },
      userCanPark(){
        if(this.user){
          const {myExtension} = this.user;
          if(!myExtension){
            return false;
          }
          return !!myExtension.enableParking;
        }
        return false;
      }
    },
    watch: {
      number(){
        //strip special characters
        this.number = this.number.replace(/['":/{}()[|\-.<>?_=+ \]]/g, '');
        if(!this.number.length) {
          this.selectedPerson = null
        }
      }
    },
    methods: {
      handleClose() {
        this.clearContact();
      },
      updateInput(value) {
        this.number = this.number + value;
      },
      async suggestContacts(queryString, cb) {

        const searchTerm = (queryString + '').replace(/[\\"']/g, '\\$&').replace(/\u0000/g, '\\0')
        //grab all entities that can use extension dialing, not just extensions
        const directory = await feathersClient.service('extensions').find({
          query: {
            entityType: 'all',
            tenantId: this.user.myExtension.tenantId,
            $or: [
              {number: {$like: `%${searchTerm}%`}},
              {name: {$like: `%${searchTerm}%`}}
            ],
            $limit: 25
          }
        });

        let personal = queryString ? this.personalDirectory.filter(this.filterPersonal(queryString)) : this.personalDirectory;
        const results = [...directory, ...personal];
        this.currentSuggestions = results;
        this.$refs.autoComplete.highlightedIndex = 0;
        cb(results);
      },
      filterPersonal(queryString){
        return (contact) => {
          return (contact.NAME.toLowerCase().indexOf(queryString.toLowerCase()) === 0 || contact.PHONE1.toLowerCase().indexOf(queryString.toLowerCase()) === 0);
        };
      },
      handleSelect(item) {
        this.selectedPerson = item;
        if(item.hasOwnProperty('PHONE1')){
          this.number = item.PHONE1;
          this.name = item.NAME;
        } else{
          this.number = item.number;
          this.name = item.name;
        }
      },
      clearContact(){
        this.number = '';
        this.name = '';
        this.selectedPerson = null
      },
      dialCall(clickToDial = false){
        //return a clean number while storing and DTMF
        const { number } = this.storeDTMF();

        //strip special characters
        this.number = number;

        if(this.softphoneEnabled){
          globalEventBus.$emit('dialSoftphone', this.number);
          this.handleClose();
        } else {
          const {peerName} = this.user.myExtension;
          feathersClient.service('nats-actions-handler').create({
            reqType: 'notifyOriginate',
            number: this.number,
            peerName
          });

          //cannot dial DTMF on hard phone so just drop these from the store if exists
          this.$store.commit('removeDTMFNumbers', number);
          this.handleClose();
        }

        if(!clickToDial) {
          globalEventBus.$emit('crmWidgetDial', number);
        }

      },
      storeDTMF(){
        //this function returns a clean number to dial and stores any DTMF in the store
        //sanitize any characters we do not consider
        this.number = this.number.replace(/['":/{}()[|\-.<>?_=+ \]]/g, '');

        //checking for a delimiter of type comma
        if(this.number.includes(',')){

          const numberSplit = this.number.split(',');
          const dtmf = [];

          for(let i = 1; i < numberSplit.length; i++){
            dtmf.push(numberSplit[i]);
          }

          //store the dtmf on this number
          const storePayload = {};
          storePayload[numberSplit[0]] = dtmf;
          this.$store.commit('addDTMFPayload', storePayload);

          return {
            number: numberSplit[0],
            dtmf
          }

        }

        //checking for delimiter of type semicolon
        if(this.number.includes(';')){
          const numberSplit = this.number.split(';');
          const dtmf = [];

          for(let i = 1; i < numberSplit.length; i++){
            dtmf.push(numberSplit[i]);
          }

          //store the dtmf on this number
          const storePayload = {};
          storePayload[numberSplit[0]] = dtmf;
          this.$store.commit('addDTMFPayload', storePayload);

          return {
            number: numberSplit[0],
            dtmf
          }
        }

        return {
          number: this.number,
          dtmf: []
        }

      },

      handleClickToDial(number) {
        if(number.charAt(0) === '1' || number.charAt(0) === '+'){
          number = number.replace(/\+/, '');
          number = number.replace(/1/, '');
        }

        this.number = number;
        this.dialCall(true);
      }
    },
    created(){
      globalEventBus.$on('salesForceClickToDial', number => {
        this.handleClickToDial(number);
      });

      globalEventBus.$on('hubspotClickToDial', number => {
        this.handleClickToDial(number);
      });

    },

    mounted(){

      this.inputElement = document.getElementById('number-autocomplete').querySelector('input');

      window.addEventListener('keypress', (event) => {
        if(this.inputElement){
          if(this.inputElement === document.activeElement){
            if(event.key === 'Enter'){
              if(this.selectedPerson){
                this.dialCall();
              } else if(this.number.length && this.currentSuggestions.length){
                this.handleSelect(this.currentSuggestions[0]);
                this.$refs.autoComplete.suggestions = [];
              } else {
                if(this.number.length && !this.currentSuggestions.length){
                  this.dialCall();
                }
              }
            }
          }
        }
      });

    },
    beforeDestroy(){
      window.removeEventListener('keypress', () => {
        console.log('keypress listener killed');
      });
    }
  }
</script>

<style lang="scss" scoped>

  @import '../../../../../assets/scss/variables';
  @import '../../../../../assets/scss/mixins';

  .contact-item{
    padding: 3px 0;
    .number-details{
      margin-left: 0 !important;
    }
  }

  .contact-container{
    height: 40px;
  }


  .header-input {
    width: 85%;
    margin: 0 auto;
  }

  .button-container {
    text-align: center;
    padding: 0 0 0 12px;
    display: flex;
    justify-content: center;
    margin-left: auto;
  }

  .button-wrapper {
    //margin: 10px 10px 0;
  }

  .el-autocomplete{
    width: 100%;
  }

  .contact-info {
    margin: 10px;

    .checkbox-wrapper {
      display: flex;
      justify-content: center;
    }

    .el-button {
      color: $voxo-blue;
      font-size: 15px;
    }

    ::placeholder {
      text-align: left;
      font-size: 17px;
    }

  }
</style>
